import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { getMessaging, getToken, isSupported } from "firebase/messaging";

import { firebase, apiUrl } from "environments/environment.helper";
import { tap, catchError, of, BehaviorSubject, Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { initializeApp } from "firebase/app";
import { AngularDeviceInformationService } from "angular-device-information";

const app = initializeApp(firebase);

/** localStorage key holding this browser's stable device id. */
const DEVICE_ID_KEY = "aem_device_id";

@Injectable({
  providedIn: "root",
})
export class FirebaseNotificationService {
  title = "notification";
  message: any = null;

  public notificationToken: WritableSignal<string> = signal(null);

  private readonly _deviceInformation = inject(AngularDeviceInformationService);

  constructor(private readonly _httpClient: HttpClient) {}

  /**
   * Stable per-browser identifier, generated once and kept in localStorage so it
   * survives FCM token refresh and JWT rotation. Used as the device-token key.
   */
  private getDeviceId(): string {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  }

  /** Human-readable device description, e.g. "Chrome 126 · macOS 10". */
  private getDeviceInfoLabel(): string {
    try {
      const d = this._deviceInformation.getDeviceInfo();
      return [
        [d.browser, d.browserVersion].filter(Boolean).join(" "),
        [d.os, d.osVersion].filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(" · ");
    } catch {
      return navigator.userAgent;
    }
  }

  async checkSupport(): Promise<boolean> {
    try {
      if (
        typeof window === "undefined" ||
        !("Notification" in window) ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window)
      ) {
        return false;
      }
      return await isSupported();
    } catch (error) {
      console.warn("FCM isSupported check failed:", error);
      return false;
    }
  }

  async getToken(): Promise<string | undefined> {
    try {
      const supported = await this.checkSupport();
      if (!supported) {
        console.log("Firebase Messaging is not supported in this browser/device environment.");
        return undefined;
      }

      const messaging = getMessaging(app);

      // FCM needs ITS OWN service worker (firebase-messaging-sw.js). Nothing
      // else registers it — getRegistration() would return Angular's ngsw
      // worker (wrong scope) or nothing, so getToken() would fail and the
      // device token would be saved as null. Register it explicitly; register()
      // is idempotent and returns the existing registration if already active.
      const serviceWorkerRegistration = await navigator.serviceWorker.register(
        "firebase-messaging-sw.js",
        { scope: "/firebase-cloud-messaging-push-scope" },
      );

      const currentToken = await getToken(messaging, {
        vapidKey: firebase.vapidKey,
        serviceWorkerRegistration: serviceWorkerRegistration,
      });

      if (currentToken) {
        this.notificationToken.set(currentToken);
        return currentToken;
      }

      console.log(
        "No registration token available. Request permission to generate one."
      );
      return undefined;
    } catch (err) {
      console.error("An error occurred while retrieving token. ", err);
      return undefined;
    }
  }

  saveToken<SaveToken>(token?: string, notificationStatus?: string) {
    const deviceId = this.getDeviceId();
    const deviceInfo = this.getDeviceInfoLabel();
    const params = new URLSearchParams({
      deviceToken: token ?? "",
      deviceId,
      deviceInfo,
      NotificationState: notificationStatus ?? "",
    });

    return this._httpClient
      .post<SaveToken>(
        `${apiUrl}/Notification/SaveDeviceToken?${params.toString()}`,
        null
      )
      .pipe(
        tap((res) => {
          return res;
        }),
        catchError((err) => of(err))
      );
  }
}
